import React, { useCallback, useEffect, useState } from "react";
import FormEntryPoint from "./form/FormEntryPoint";
import TurnrForm from "../input/TurnrForm.jsx";
import { serviceHasError, serviceIsLoading, useServiceHook } from "rylak-common";
import { ServiceErrorAlert } from "../Error.jsx";
import { SkeletonLoaderWrapper } from "~/components/loading/skeletons/SkeletonLoaderWrapper.jsx";
import TurnrTableSmart from "~/components/table/TurnrTableSmart.jsx";
import { useTableSortQuery } from "~/components/table/useTableSortQuery.jsx";
import { useTablePageState, useTableSizeState } from "~/hooks";
import EntityActionCreateButton from "~/components/entityAction/EntityActionCreateButton.jsx";
import EntityActionsDotsMenu from "~/components/entityAction/EntityActionsDotsMenu.jsx";
import SearchAndFilterComponent from "~/app/(pages)/returns/SearchAndFilterComponent";
import { fetchEntityTypeSpecification, fetchFilteredEntitiesPaginated } from "~/services.js";

export default function EntityActionFilterForm(props) {
    const {
        appProps,

        entityType,

        loadingMinHeight = "72px",
        loadingMinWidth = "400px",

        tableHeadData,
        rightComponents,
        children,
    } = props;
    const { userData } = appProps;

    const [page, setPage] = useTablePageState();
    const [size] = useTableSizeState();

    let { serviceData: entitiesPaginatedService, fetchServiceData: fetchEntitiesPaginatedFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchData: {
            entityType: entityType,
        },
        fetchFn: fetchFilteredEntitiesPaginated,
    });

    let { serviceData: entityTypeSpecificationServiceData } = useServiceHook({
        appProps,
        fetchRequested: true,
        fetchData: {
            entityType,
        },
        fetchFn: fetchEntityTypeSpecification,
    });

    const loading = serviceIsLoading(entityTypeSpecificationServiceData);

    const filtersFormParameters = entityTypeSpecificationServiceData?.data?.specification?.filter?.filterParameters;
    const searchParameter = entityTypeSpecificationServiceData?.data?.specification?.filter?.searchParameter;
    const entityActions = entityTypeSpecificationServiceData?.data?.specification?.actions;

    // We update the jsonObject without updating the state for performance reasons. Might want to look into changing this
    const [filterFormValues, setFilterFormValues] = useState(null);
    const [searchFormValues, setSearchFormValues] = useState(null);

    const specData = entityTypeSpecificationServiceData?.data;

    useEffect(() => {
        if (specData != null && filterFormValues === null) {
            setFilterFormValues({});
        }
        if (specData != null && searchFormValues === null) {
            setSearchFormValues({});
        }
    }, [specData, filterFormValues, searchFormValues]);

    let tableSortData = useTableSortQuery(tableHeadData);
    const { sort } = tableSortData;

    const reFetchEntitiesPaginated = useCallback(() => {
        if (filterFormValues && searchFormValues) {
            fetchEntitiesPaginatedFn(
                {
                    filter: { ...filterFormValues, ...searchFormValues },
                    page,
                    size,
                    sort,
                },
                { silentFetch: true },
            );
        }
    }, [page, size, sort, userData, filterFormValues, searchFormValues]);

    useEffect(() => {
        reFetchEntitiesPaginated();
    }, [reFetchEntitiesPaginated]);

    const childrenWithProps = React.Children.map(children, (child) => {
        // Checking isValidElement is the safe way and avoids a
        // typescript error too.
        if (React.isValidElement(child)) {
            return React.cloneElement(child, {
                entitiesPaginatedService,
                reFetchEntitiesPaginated,
            });
        }
        return child;
    });

    return serviceHasError(entityTypeSpecificationServiceData) ? (
        <ServiceErrorAlert serviceData={entityTypeSpecificationServiceData} />
    ) : (
        <div>
            <TurnrForm
                onSubmit={() => {
                    // triggerAction(entityAction, filterFormValuesForService(entityAction, { formValues }));
                }}
            >
                <SkeletonLoaderWrapper loading={loading} loadingMinHeight={loadingMinHeight} loadingMinWidth={loadingMinWidth} slim>
                    {filtersFormParameters && filterFormValues && (
                        <div>
                            <SearchAndFilterComponent
                                appProps={appProps}
                                searchComponent={<FormEntryPoint appProps={appProps} formParameters={[searchParameter]} formValues={searchFormValues} setFormValues={setSearchFormValues} />}
                                rightComponents={[
                                    // eslint-disable-next-line
                                    <EntityActionCreateButton appProps={appProps} entityActions={entityActions} onSuccess={reFetchEntitiesPaginated} />,
                                    ...(rightComponents ?? []),
                                ]}
                                dotsMenu={<EntityActionsDotsMenu key="entity-actions-dots-menu" appProps={appProps} entityActions={entityActions} onSuccess={reFetchEntitiesPaginated} />}
                                // filterComponents={}
                            >
                                {filtersFormParameters?.length > 0 && (
                                    <FormEntryPoint
                                        appProps={appProps}
                                        formParameters={filtersFormParameters}
                                        formValues={filterFormValues}
                                        setFormValues={setFilterFormValues}
                                        formProps={{ direction: "row", alignItems: "center" }}
                                        formParametersSelectorProps={{ xs: 12, sm: 6, lg: 3, xl: 2 }}
                                    />
                                )}
                            </SearchAndFilterComponent>
                        </div>
                    )}
                </SkeletonLoaderWrapper>
            </TurnrForm>
            <TurnrTableSmart
                cellSize={"small"}
                tableHeadData={tableHeadData}
                tableSortData={tableSortData}
                page={page}
                setPage={setPage}
                size={size}
                serviceData={entitiesPaginatedService}
                loading={serviceIsLoading(entityTypeSpecificationServiceData)}
            >
                {childrenWithProps}
            </TurnrTableSmart>
        </div>
    );
}
